import { RefObject, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { Page } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import { Rnd } from 'react-rnd'

import { Box } from '@factorialco/deprecated-design-system'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import SignerStamp from 'modules/documents/components/EsignaturePlacementFile/SignerStamp'
import {
  DEFAULT_PAGE_HEIGHT_MARGIN,
  DEFAULT_PAGE_WIDTH_MARGIN,
  DragStamp,
  DropTypes,
  MIN_HEIGHT_SIGNER_STAMP,
  MIN_WIDTH_SIGNER_STAMP,
  Stamp,
} from 'modules/documents/components/EsignaturePlacementFile/constants'

interface Props {
  pageNumber: number
  stamps: Stamp[]
  pageWidth: number
  pageHeight: number
  onDrop: (signerId: number, pageNumber: number, x: number, y: number, scale: number) => void
  onDrag: (id: string, x: number, y: number) => void
  onResize: (id: string, width: number, height: number) => void
  onRemove: (id: string, signerId: number, page: number) => void
  parentRef: RefObject<HTMLDivElement>
}

const DroppablePdfPage = ({
  pageNumber,
  stamps,
  pageWidth,
  pageHeight,
  onDrop,
  onDrag,
  onResize,
  onRemove,
  parentRef,
}: Props) => {
  const pageContainer = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(1)
  const [, drop] = useDrop({
    accept: DropTypes.STAMP,
    drop: (item: DragStamp, monitor) => {
      const offset = monitor.getClientOffset()
      const container = pageContainer.current ? pageContainer.current.getBoundingClientRect() : null
      const x = offset && container ? offset.x - container.left : 0
      const y = offset && container ? offset.y - container.top : 0

      onDrop(item.signerId, pageNumber, x, y, scale)
    },
  })

  useUnsafeEffect(() => {
    const calculateScale = () => {
      if (parentRef.current && pageWidth) {
        const parentWidth = parentRef.current.clientWidth - DEFAULT_PAGE_WIDTH_MARGIN * 2
        const newScale = parentWidth / pageWidth

        setScale(newScale)
      }
    }

    calculateScale()
  }, [parentRef, pageWidth])

  const pageRef = (node: HTMLDivElement) => {
    pageContainer.current = node
    drop(node)
  }

  return (
    <Box
      ref={pageRef}
      style={{
        position: 'relative',
        width: `${pageWidth * scale}px`,
        height: `${pageHeight * scale}px`,
        marginTop: `${DEFAULT_PAGE_HEIGHT_MARGIN}px`,
        marginBottom: `${DEFAULT_PAGE_HEIGHT_MARGIN}px`,
      }}
    >
      <Page pageNumber={pageNumber} scale={scale} renderTextLayer={false} />
      {stamps.map((stamp) => (
        <Rnd
          key={stamp.id}
          scale={1}
          size={{ width: stamp.width, height: stamp.height }}
          position={{ x: stamp.x, y: stamp.y }}
          bounds="parent"
          enableResizing={{
            top: false,
            right: false,
            bottom: false,
            left: false,
            topRight: false,
            bottomRight: true,
            topLeft: false,
            bottomLeft: false,
          }}
          resizeHandleStyles={{
            bottomRight: { bottom: '0', right: '0' },
          }}
          minWidth={MIN_WIDTH_SIGNER_STAMP * scale}
          minHeight={MIN_HEIGHT_SIGNER_STAMP * scale}
          onDragStop={(_e, d) => {
            onDrag(stamp.id, d.x, d.y)
          }}
          onResizeStop={(_e, _direction, ref, _delta, _position) => {
            onResize(stamp.id, parseInt(ref.style.width), parseInt(ref.style.height))
          }}
          style={{ zIndex: 2 }}
        >
          <SignerStamp
            fullName={stamp.signer.fullName}
            avatar={stamp.signer.avatar}
            jobTitle={stamp.signer.jobTitle}
            status="editable"
            isPending={false}
            onRemove={() => onRemove(stamp.id, stamp.signer.id, stamp.page.number)}
          />
        </Rnd>
      ))}
    </Box>
  )
}

export default DroppablePdfPage
